import { Calendar, Mail, Users, ArrowUpRight, Sparkles, MapPin, Ticket, Award, Star, Building2, ChevronDown, ChevronUp, Check, Zap, Globe, Shield, Gift, Clock, Plus, Minus, CreditCard } from 'lucide-react';
import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const contact = 'marketing@getzurihub.com';

const ticketState = {
  quantity: 1,
  price: 95,
  currency: 'USD',
  total: 95,
};

const currencies = [
  { code: 'USD', symbol: '$', rate: 1 },
  { code: 'UGX', symbol: 'UGX', rate: 3750 },
  { code: 'KES', symbol: 'KES', rate: 130 },
  { code: 'EUR', symbol: '€', rate: 0.92 },
];

const webinar = {
  title: 'Getting to Know ZuriHub and Live Demos',
  date: '30th November 2026',
  time: '3:30 PM – 4:15 PM',
  audience: 'First 50 participants free',
  gift: '$15 gift card for participants',
  description: 'A live, no-fluff introduction to ZuriHub. See how unified business communication transforms customer experience, reduces support costs, and improves response times.',
};

const upcomingEvent = {
  title: 'ZuriConnect Edition One',
  subtitle: 'July 31st, 2027 • Live in Jinja',
  description: 'Join East Africa\'s premier customer experience conference connecting business leaders, support teams, and communication professionals. Discover strategies to enhance customer engagement and build lasting relationships.',
  location: 'Jinja, Uganda',
  date: 'July 31st, 2027',
  ticketPrice: '$95',
  ticketIncludes: ['Conference access', 'Free snacks and refreshments', 'Exhibition booth', 'Networking sessions', 'Swag bag', 'Certificate of attendance'],
};

const sponsors = [
  { name: 'MTN', tier: 'Gold', logo: 'https://media.getzurihub.com/Jayrah%20Assets/MoMO.jpeg' },
  { name: 'Cloudflare', tier: 'Gold', logo: 'https://media.getzurihub.com/Jayrah%20Assets/Cloudflare.png' },
  { name: 'Google Cloud', tier: 'Gold', logo: 'https://www.gstatic.com/bricks/image/720624ce-2196-48d4-9963-a31aa86bdfbc.webp' },
  { name: 'PayPal', tier: 'Gold', logo: 'https://media.getzurihub.com/Jayrah%20Assets/PayPal.png' },
  { name: 'Akamai Cloud', tier: 'Silver', logo: 'https://www.linode.com/linode/en/images/logo/akamai-cloud-logo.svg' },
  { name: 'AWS', tier: 'Silver', logo: 'https://media.getzurihub.com/Jayrah%20Assets/AWS.png' },
  { name: 'Infobip', tier: 'Silver', logo: 'https://media.getzurihub.com/Jayrah%20Assets/a_info.jpeg' },
  { name: 'Meta', tier: 'Silver', logo: 'https://media.getzurihub.com/Jayrah%20Assets/Meta-Logo.png' },
  { name: 'Meizon Radio', tier: 'Silver', logo: 'https://media.getzurihub.com/Jayrah%20Assets/Meizon%20Radio.png' },
  { name: 'AppNomu SalesQ', tier: 'Silver', logo: 'https://appnomu.com/landing/assets/images/5_Years.jpeg' },
  { name: 'CodeLab', tier: 'Silver', logo: 'https://codelab.appnomu.com/assets/images/CMODERN.png' },
];

const sponsorshipTiers = [
  {
    tier: 'Gold',
    price: '$1,500',
    color: 'from-yellow-500 to-amber-600',
    features: [
      'Premium booth placement',
      'Logo on all event materials',
      'Speaking opportunity',
      'VIP tickets (5)',
      'Social media promotion',
      'Post-event attendee list',
      'Exclusive networking dinner',
    ],
  },
  {
    tier: 'Silver',
    price: '$950',
    color: 'from-slate-400 to-slate-500',
    features: [
      'Standard booth placement',
      'Logo on website and program',
      'Speaking opportunity',
      'VIP tickets (3)',
      'Social media mention',
      'Networking access',
    ],
  },
  {
    tier: 'Bronze',
    price: '$500',
    color: 'from-orange-600 to-orange-700',
    features: [
      'Booth placement',
      'Logo on website',
      'Standard tickets (2)',
      'Networking access',
      'Event swag inclusion',
    ],
  },
];

const whySponsor = [
  {
    icon: Users,
    title: 'Reach 500+ attendees',
    description: 'Connect with customer experience leaders, support professionals, and business decision-makers from across East Africa.',
  },
  {
    icon: Building2,
    title: 'Brand visibility',
    description: 'Showcase your customer communication solutions to business leaders focused on improving customer experience.',
  },
  {
    icon: Globe,
    title: 'Regional impact',
    description: 'Position your brand as a leader in East Africa\'s growing customer experience and business communication landscape.',
  },
  {
    icon: Star,
    title: 'Networking opportunities',
    description: 'Build relationships with potential partners, customers, and CX professionals in an engaging environment.',
  },
  {
    icon: Award,
    title: 'Thought leadership',
    description: 'Demonstrate expertise through speaking sessions and panel discussions on customer experience best practices.',
  },
  {
    icon: Shield,
    title: 'Measurable ROI',
    description: 'Track engagement through lead capture, booth interactions, and post-event attendee follow-up.',
  },
];

const eventHighlights = [
  { icon: Zap, title: 'Keynote Sessions', text: 'Industry leaders share insights on customer experience, communication strategies, and business growth.' },
  { icon: Users, title: 'Networking', text: 'Connect with CX professionals, support leaders, and business experts in structured sessions.' },
  { icon: Building2, title: 'Exhibition', text: 'Showcase your customer communication solutions to business leaders and CX professionals.' },
  { icon: Sparkles, title: 'Workshops', text: 'Hands-on sessions covering customer experience best practices and communication strategies.' },
];

const faqs = [
  {
    q: 'What is included in the $95 ticket?',
    a: 'The ticket includes full conference access, free snacks and refreshments throughout the event, an exhibition booth for your company or personal projects, networking sessions, a swag bag, and a certificate of attendance.',
  },
  {
    q: 'Where will ZuriConnect Edition One be held?',
    a: 'The event will be held live in Jinja, Uganda. Jinja is known as the adventure capital of East Africa and provides a perfect backdrop for customer experience innovation and networking.',
  },
  {
    q: 'How can I become a sponsor?',
    a: 'To become a sponsor, email marketing@getzurihub.com with your sponsorship tier preference (Gold $1,500, Silver $950, or Bronze $500). Our team will provide you with a sponsorship package and next steps.',
  },
  {
    q: 'What are the different sponsorship tiers?',
    a: 'We offer three sponsorship tiers: Gold ($1,500) with premium benefits including speaking opportunities and VIP tickets, Silver ($950) with standard booth and networking access, and Bronze ($500) with basic booth placement and event tickets.',
  },
  {
    q: 'Who are the current sponsors?',
    a: 'Our Gold sponsors include MTN, Cloudflare, Google Cloud, and PayPal. Silver sponsors include Akamai Cloud, AWS, Infobip, Meta, Meizon Radio, AppNomu SalesQ, and CodeLab.',
  },
  {
    q: 'Can I get a refund if I can\'t attend?',
    a: 'Refunds are available up to 30 days before the event. Contact marketing@getzurihub.com for refund requests and transfer options.',
  },
  {
    q: 'When is the next webinar?',
    a: 'The upcoming webinar is on 30th November 2026 from 3:30 PM to 4:15 PM. It\'s free for the first 50 participants.',
  },
  {
    q: 'What is the webinar about?',
    a: 'The webinar covers a live introduction to ZuriHub with product demos focused on customer experience transformation. You\'ll see how unified business communication improves support efficiency and customer satisfaction.',
  },
  {
    q: 'How do I get the webinar gift card?',
    a: 'Attend the live webinar and stay until the end. Gift cards are sent within 5 working days, depending on the recipient region and the nature of gift cards available in such regions.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ q: string; a: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.q}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: webinar.title,
    startDate: '2026-11-30T15:30:00+03:00',
    endDate: '2026-11-30T16:15:00+03:00',
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: { '@type': 'VirtualLocation', url: `${site.baseUrl}/events` },
    description: webinar.description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock', validFrom: '2026-08-25' },
    organizer: { '@type': 'Organization', name: site.name, url: site.baseUrl, email: contact },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: upcomingEvent.title,
    startDate: '2027-07-31',
    endDate: '2027-07-31',
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: upcomingEvent.location,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Jinja',
        addressCountry: 'UG',
      },
    },
    description: upcomingEvent.description,
    offers: {
      '@type': 'Offer',
      price: '95',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      validFrom: '2026-08-25',
    },
    organizer: {
      '@type': 'Organization',
      name: site.name,
      url: site.baseUrl,
      email: contact,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

export default function Events() {
  const [searchParams] = useSearchParams();
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [selectedCurrency, setSelectedCurrency] = useState(currencies[0]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'cancelled' | null>(null);

  // Handle payment callback
  useEffect(() => {
    const status = searchParams.get('status');
    const txRef = searchParams.get('tx_ref');

    if (status && txRef) {
      // Remove status from URL to prevent duplicate processing
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      if (status === 'success') {
        setPaymentStatus('success');
        // Redirect to main events page after showing success message
        setTimeout(() => {
          window.location.href = `${site.baseUrl}/events`;
        }, 3000);
      } else if (status === 'cancelled' || status === 'failed') {
        setPaymentStatus('cancelled');
        // Redirect to main events page after showing cancellation message
        setTimeout(() => {
          window.location.href = `${site.baseUrl}/events`;
        }, 3000);
      }
    }
  }, [searchParams]);

  const updateQuantity = (delta: number) => {
    const newQuantity = Math.max(1, Math.min(10, ticketQuantity + delta));
    setTicketQuantity(newQuantity);
  };

  const handleCurrencyChange = (currencyCode: string) => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currency) {
      setSelectedCurrency(currency);
    }
  };

  const calculateTotal = () => {
    const baseAmount = ticketQuantity * ticketState.price;
    return baseAmount * selectedCurrency.rate;
  };

  const formatPrice = (amount: number) => {
    if (selectedCurrency.code === 'USD') {
      return `$${amount}`;
    } else if (selectedCurrency.code === 'EUR') {
      return `€${amount.toFixed(2)}`;
    } else {
      // UGX and KES
      return `${selectedCurrency.symbol} ${Math.round(amount).toLocaleString()}`;
    }
  };

  const handlePurchase = async () => {
    // Form validation
    if (!userEmail || !userName) {
      alert('Please provide your email and name to complete the purchase.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      alert('Please provide a valid email address.');
      return;
    }

    // Name validation
    if (userName.trim().length < 2) {
      alert('Please provide your full name (at least 2 characters).');
      return;
    }

    setIsProcessing(true);
    try {
      // Integrate with payment infrastructure
      const response = await fetch(`${site.apiUrl}/api/payment/event-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_id: 'zuri-connect-edition-one',
          quantity: ticketQuantity,
          amount: calculateTotal(),
          currency: selectedCurrency.code,
          user_email: userEmail,
          user_name: userName,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_link) {
          // Redirect to payment gateway
          window.location.href = data.payment_link;
        } else {
          // Handle successful payment
          alert('Ticket purchase successful! Check your email for confirmation.');
        }
      } else {
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <SEO
        title="Events - Webinars & ZuriConnect Conference"
        description="Join our live webinars and ZuriConnect Edition One customer experience conference in Jinja, July 31st, 2027. Free webinar with $15 gift card for first 50 participants. Conference tickets $95 with online payment via ZuriWallet."
        path="/events"
        jsonLd={jsonLd}
      />

      {/* Payment Status Messages */}
      {paymentStatus === 'success' && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-green-50 border-b border-green-200 px-4 py-4">
          <div className="mx-auto max-w-6xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-600" />
              <p className="text-sm font-semibold text-green-900">Payment successful! Check your email for ticket confirmation.</p>
            </div>
            <p className="text-xs text-green-700">Redirecting to events page...</p>
          </div>
        </div>
      )}

      {paymentStatus === 'cancelled' && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-50 border-b border-yellow-200 px-4 py-4">
          <div className="mx-auto max-w-6xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-yellow-600" />
              <p className="text-sm font-semibold text-yellow-900">Payment cancelled. You can try again anytime.</p>
            </div>
            <p className="text-xs text-yellow-700">Redirecting to events page...</p>
          </div>
        </div>
      )}

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Calendar className="h-4 w-4" /> Webinars & Conferences
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Events & <span className="gradient-text">Conferences</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Join our live webinars and ZuriConnect Edition One conference. Learn from customer experience experts, network with business leaders, and discover strategies to transform your customer communication.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="https://luma.com/2466w3rn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Join Webinar <Gift className="h-4 w-4" />
                </a>
                <a
                  href="#conference-tickets"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Conference Tickets <Ticket className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative grid gap-4">
                {[
                  { icon: Sparkles, label: 'Live Webinars', color: 'from-blue-500 to-blue-600' },
                  { icon: MapPin, label: 'Jinja Conference', color: 'from-purple-500 to-purple-600' },
                  { icon: Users, label: '500+ Attendees', color: 'from-green-500 to-green-600' },
                  { icon: Award, label: 'Gift Cards', color: 'from-orange-500 to-orange-600' },
                ].map((item, i) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 backdrop-blur"
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${item.color} text-white shadow-lg`}>
                      <item.icon className="h-6 w-6" />
                    </div>
                    <span className="font-semibold text-white">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="webinar-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-sm font-semibold text-slate-700">
              <Sparkles className="h-4 w-4" /> Upcoming Webinar
            </span>
            <h2 id="webinar-heading" className="section-title mt-6">Live Webinar: Getting to Know ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Join our live demo and Q&A session. Free for the first 50 participants with a $15 gift card.
            </p>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm lg:p-12">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">{webinar.title}</h3>
                <p className="mt-3 text-slate-600">
                  {webinar.description}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                    <Calendar className="h-4 w-4" /> {webinar.date}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                    <Clock className="h-4 w-4" /> {webinar.time}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                    <Users className="h-4 w-4" /> {webinar.audience}
                  </span>
                </div>
                <a
                  href="https://luma.com/2466w3rn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-600 px-6 py-3 text-base font-bold text-white transition hover:bg-brand-700 shadow-lg"
                >
                  Register Now <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
              <div className="rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 p-8 text-white shadow-2xl">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
                  <Gift className="h-8 w-8" />
                </div>
                <h4 className="mt-6 text-2xl font-bold">{webinar.gift}</h4>
                <p className="mt-3 text-brand-100">
                  Join live and stay until the end. Gift cards are sent within 5 working days, depending on the recipient region and the nature of gift cards available in such regions.
                </p>
                <div className="mt-6 rounded-xl bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">🎁 Limited to first 50 participants</p>
                  <p className="text-sm text-brand-200 mt-1">Register early to secure your spot and gift card.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="highlights-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="highlights-heading" className="section-title">What to expect</h2>
            <p className="section-subtitle mx-auto mt-4">A comprehensive tech experience designed for growth and connection.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {eventHighlights.map((highlight) => (
              <div key={highlight.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <highlight.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{highlight.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{highlight.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="conference-tickets" className="bg-slate-50 py-24" aria-labelledby="ticket-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="ticket-heading" className="section-title">Get your ticket</h2>
            <p className="section-subtitle mx-auto mt-4">Secure your spot at East Africa's premier customer experience conference.</p>
          </div>
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <Ticket className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">Conference Ticket</h3>
                  <p className="text-sm text-slate-500">Full access to all events</p>
                </div>
              </div>
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <label className="text-sm font-semibold text-slate-700">Currency:</label>
                  <select
                    value={selectedCurrency.code}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    {currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-slate-900">{formatPrice(ticketState.price * selectedCurrency.rate)}</span>
                  <span className="text-lg text-slate-500">/person</span>
                </div>
                <p className="mt-2 text-sm text-slate-500">One-time payment • All-inclusive</p>
              </div>
              <ul className="space-y-3 mb-8">
                {upcomingEvent.ticketIncludes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 text-green-600 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mb-6 rounded-xl bg-slate-50 p-4 border border-slate-200">
                <p className="text-sm font-semibold text-slate-900 mb-3">Select Quantity</p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => updateQuantity(-1)}
                    disabled={ticketQuantity <= 1}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="text-2xl font-bold text-slate-900 w-12 text-center">{ticketQuantity}</span>
                  <button
                    onClick={() => updateQuantity(1)}
                    disabled={ticketQuantity >= 10}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-2">Maximum 10 tickets per purchase</p>
              </div>
              <div className="mb-6 flex items-center justify-between rounded-xl bg-brand-50 p-4 border border-brand-200">
                <span className="text-sm font-semibold text-slate-900">Total Amount</span>
                <span className="text-2xl font-bold text-brand-700">{formatPrice(calculateTotal())}</span>
              </div>
              <div className="mb-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Your Email *</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full rounded-lg border border-slate-300 px-4 py-3 pl-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      required
                    />
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Your Name *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Enter your full name"
                      className="w-full rounded-lg border border-slate-300 px-4 py-3 pl-10 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      required
                    />
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>
              <button
                onClick={handlePurchase}
                disabled={isProcessing}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-6 py-3 text-base font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <CreditCard className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Purchase Tickets <ArrowUpRight className="h-4 w-4" />
                  </>
                )}
              </button>
              <p className="text-xs text-slate-500 text-center mt-3">
                Secure payment powered by ZuriWallet • Confirmation email will be sent upon purchase
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                  <MapPin className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">Event Details</h3>
                  <p className="text-sm text-slate-500">Plan your attendance</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <Calendar className="h-5 w-5 text-brand-600" />
                  <div>
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="font-semibold text-slate-900">{upcomingEvent.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <MapPin className="h-5 w-5 text-brand-600" />
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="font-semibold text-slate-900">{upcomingEvent.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <Users className="h-5 w-5 text-brand-600" />
                  <div>
                    <p className="text-sm text-slate-500">Expected Attendees</p>
                    <p className="font-semibold text-slate-900">500+ professionals</p>
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4 border border-slate-200">
                  <p className="text-sm text-slate-800 font-medium">🎉 Early bird special</p>
                  <p className="text-sm text-slate-600 mt-1">Register early to secure your spot and get exclusive updates.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="sponsors-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="sponsors-heading" className="section-title">Our Sponsors</h2>
            <p className="section-subtitle mx-auto mt-4">Partnering with industry leaders to deliver an exceptional experience.</p>
          </div>
          <div className="mb-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Gold Sponsors</h3>
            <div className="grid gap-6 sm:grid-cols-3">
              {sponsors.filter(s => s.tier === 'Gold').map((sponsor) => (
                <div key={sponsor.name} className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm text-center transition hover:-translate-y-1 hover:shadow-lg">
                  <img 
                    src={sponsor.logo} 
                    alt={sponsor.name}
                    className="h-16 w-auto mx-auto mb-4 object-contain"
                  />
                  <h4 className="text-lg font-bold text-slate-900">{sponsor.name}</h4>
                  <p className="text-sm text-slate-500 font-semibold mt-1">Gold Sponsor</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Silver Sponsors</h3>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {sponsors.filter(s => s.tier === 'Silver').map((sponsor) => (
                <div key={sponsor.name} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-center transition hover:-translate-y-1 hover:shadow-lg">
                  <img 
                    src={sponsor.logo} 
                    alt={sponsor.name}
                    className="h-12 w-auto mx-auto mb-3 object-contain"
                  />
                  <h4 className="font-bold text-slate-900">{sponsor.name}</h4>
                  <p className="text-xs text-slate-500 mt-1">Silver Sponsor</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="why-sponsor-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="why-sponsor-heading" className="section-title">Why Sponsor ZuriConnect?</h2>
            <p className="section-subtitle mx-auto mt-4">Connect with East Africa's tech ecosystem and showcase your brand.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {whySponsor.map((reason) => (
              <div key={reason.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <reason.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{reason.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="sponsorship-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="sponsorship-heading" className="section-title">Sponsorship Tiers</h2>
            <p className="section-subtitle mx-auto mt-4">Choose the partnership level that fits your goals.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {sponsorshipTiers.map((tier) => (
              <div
                key={tier.tier}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                  <Award className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{tier.tier} Sponsor</h3>
                <div className="mt-4 mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-slate-900">{tier.price}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">One-time investment</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 text-green-600 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href={`mailto:${contact}?subject=${tier.tier} Sponsorship for ZuriConnect`}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-base font-semibold text-white transition hover:bg-slate-800"
                >
                  Become {tier.tier} Sponsor <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about ZuriConnect Edition One.</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="contact-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="contact-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ready to join or sponsor?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Contact our team to secure your ticket or discuss sponsorship opportunities for ZuriConnect Edition One.
          </p>
          <a
            href={`mailto:${contact}`}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
          >
            <Mail className="h-4 w-4" /> {contact}
          </a>
        </div>
      </section>
    </>
  );
}